'use client';

import { useAppStore } from '@/lib/redux/hooks';
import { reportFrontendAlert } from '@/utils/frontendAlertReporter';
import { useEffect, useRef } from 'react';

interface MonitorRule {
  name: string;
  timeoutMs: number;
  isActive: (state: {
    isInitializing: boolean;
    isChatHistoryLoading: boolean;
    queryAnalyzing: boolean;
    streaming: boolean;
    sessionId: string | null;
  }) => boolean;
}

const POLL_INTERVAL_MS = 5_000;

const MONITOR_RULES: MonitorRule[] = [
  {
    name: 'session_init_stuck',
    timeoutMs: 15_000,
    isActive: (s) => s.isInitializing,
  },
  {
    name: 'chat_history_load_stuck',
    timeoutMs: 15_000,
    isActive: (s) => s.isChatHistoryLoading,
  },
  {
    name: 'query_analyzing_stuck',
    timeoutMs: 45_000,
    isActive: (s) => s.queryAnalyzing,
  },
];

/**
 * App-level watchdog that polls Redux state for stuck loading/streaming states.
 * Sends a one-time alert to the backend when a state exceeds its timeout.
 * Renders nothing — mount once inside StoreProvider.
 */
export function FrontendWatchdog() {
  const appStore = useAppStore();
  // Track when each state first became active: { ruleName -> timestamp }
  const activeSince = useRef<Record<string, number>>({});
  // Track which (name, sessionId) combos have already been reported
  const reported = useRef<Set<string>>(new Set());

  useEffect(() => {
    const interval = setInterval(() => {
      const { researchSession } = appStore.getState();
      const now = Date.now();

      const stateSlice = {
        isInitializing: researchSession.isInitializing,
        isChatHistoryLoading: researchSession.isChatHistoryLoading,
        queryAnalyzing: researchSession.queryAnalyzing,
        streaming: researchSession.streaming,
        sessionId: researchSession.sessionId,
      };

      for (const rule of MONITOR_RULES) {
        const key = `${rule.name}:${stateSlice.sessionId ?? 'none'}`;

        if (rule.isActive(stateSlice)) {
          // Record activation time if not already tracked
          if (!activeSince.current[key]) {
            activeSince.current[key] = now;
          }

          const elapsed = now - activeSince.current[key];

          if (elapsed >= rule.timeoutMs && !reported.current.has(key)) {
            reported.current.add(key);
            reportFrontendAlert({
              name: rule.name,
              session_id: stateSlice.sessionId,
              duration_ms: elapsed,
              url: window.location.href,
              user_agent: navigator.userAgent,
              viewport: `${window.innerWidth}x${window.innerHeight}`,
              redux_snapshot: stateSlice,
            });
          }
        } else {
          // State resolved — clear tracking
          delete activeSince.current[key];
        }
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [appStore]);

  return null;
}
